import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import type { IBlotterColumnDefinition } from 'state/blotters/common/blotter.model';
import type { SecondaryMarketProduct } from 'state/secondaryMarket/productSearch';
import type { IHttpClient } from 'utils/http/httpClient.model';

import type {
  GetProductByKeyResponse,
  ProductEditRequest,
  ProductsEditResponse,
  RecentSearchesKeysPayload,
  SearchProductsResponse,
} from './model';

export interface SearchProductsByCodeApi {
  searchProductsByCode(payload: RecentSearchesKeysPayload): Observable<SearchProductsResponse>;
}

interface IColumnDefinitionResponse {
  columnDefinitions: IBlotterColumnDefinition<SecondaryMarketProduct>[];
}

export interface SecondaryProductsColumnDefinitionApi {
  fetchColumnDefinition(): Observable<IBlotterColumnDefinition<SecondaryMarketProduct>[]>;
}

export interface SecondaryProductsRecentSearchLoadApi {
  loadRecentSearchKeys(): Observable<RecentSearchesKeysPayload>;
}

export interface SecondaryProductsRecentSearchSaveApi {
  saveRecentSearchKeys(payload: RecentSearchesKeysPayload): Observable<unknown>;
}

export interface SearchProductsByCodeApi {
  getBcsProductByKey(key: string): Observable<GetProductByKeyResponse>;
}

export interface SecondaryProductsEditProductsApi {
  editProducts(requests: ProductEditRequest[]): Observable<ProductsEditResponse>;
}

export function secondaryProductsApiConnector(
  http$: IHttpClient,
): SearchProductsByCodeApi &
  SecondaryProductsColumnDefinitionApi &
  SecondaryProductsRecentSearchLoadApi &
  SecondaryProductsRecentSearchSaveApi &
  SecondaryProductsEditProductsApi {
  return {
    searchProductsByCode: payload => {
      return http$
        .postJSON<SearchProductsResponse>({
          url: 'api/v2/secondary-market/products',
          body: payload,
          targetHost: 'SecondaryBridge',
        })
        .pipe(map(r => r.payload));
    },
    getBcsProductByKey: key => {
      return http$
        .getJSON<GetProductByKeyResponse>({
          url: `api/v1/secondary-market/products/${key}`,
          targetHost: 'SecondaryBridge',
        })
        .pipe(map(r => r.payload));
    },
    editProducts: (requests: ProductEditRequest[]) => {
      return http$
        .patch<ProductsEditResponse>({
          url: 'api/v1/secondary-market/products/edit',
          body: requests,
          tryParseErrorPayload: true,
          targetHost: 'SecondaryBridge',
        })
        .pipe(map(({ payload }) => payload));
    },
    fetchColumnDefinition: () => {
      return http$
        .getJSON<{ data: IColumnDefinitionResponse }>({
          url: 'api/v1/blotter/column-definitions/SecondaryMarketProducts',
        })
        .pipe(
          map(({ payload: { data } }) =>
            data.columnDefinitions.map(({ headerName, type, field, children, editable, hide, groupId }) => ({
              headerName,
              type,
              field,
              editable,
              suppressFillHandle: !editable,
              children,
              hide,
              groupId,
            })),
          ),
        );
    },
    saveRecentSearchKeys: payload => {
      return http$.put({
        url: 'api/v2/secondary-market/products/recentSearch',
        body: payload,
        targetHost: 'SecondaryBridge',
      });
    },
    loadRecentSearchKeys: () => {
      return http$
        .getJSON<RecentSearchesKeysPayload>({
          url: 'api/v2/secondary-market/products/recentSearch',
          tryParseErrorPayload: true,
          targetHost: 'SecondaryBridge',
        })
        .pipe(map(({ payload }) => payload));
    },
  };
}
